import { useEffect, useState } from 'react';
import { ApiClientError, dataExportApi } from '../lib/api';
import { Badge, Banner, Button, Card, CardBody, CardHeader, Field, Input, PageHeader, Spinner } from '../components/ui';
import { IconExport } from '../components/icons';

/**
 * Data Export tool — pull a client's reference data (Locations / Trades /
 * Priorities) from Corrigo or ServiceChannel and download it as CSV. Ports the
 * cp_/sc_ loop scripts: Corrigo is scoped by Branch + Customer id, SC by
 * Subscriber id.
 */

type Status = { corrigo: boolean; servicechannel: boolean } | null;

export function DataExport() {
  const [status, setStatus] = useState<Status>(null);
  const [branchId, setBranchId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [subscriberId, setSubscriberId] = useState('');
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    dataExportApi.status().then(setStatus).catch(() => setStatus({ corrigo: false, servicechannel: false }));
  }, []);

  const run = async (key: string, label: string, fn: () => Promise<number>) => {
    setBusy(key);
    setError(null);
    setNote(null);
    try {
      const rows = await fn();
      setNote(`${label}: exported ${rows} row${rows === 1 ? '' : 's'}.`);
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : `Could not export ${label}.`);
    } finally {
      setBusy(null);
    }
  };

  const corrigoReady = Boolean(status?.corrigo) && branchId.trim() !== '' && customerId.trim() !== '';
  const scReady = Boolean(status?.servicechannel) && subscriberId.trim() !== '';

  return (
    <div className="space-y-6">
      <PageHeader
        backTo="/"
        icon={<IconExport className="h-5 w-5" />}
        title="Data Export"
        subtitle="Pull a client's Locations, Trades, and Priorities and download them as CSV. Each export calls the platform API directly and streams the results to your browser."
      />

      {error && <Banner tone="error">{error}</Banner>}
      {note && <Banner tone="success">{note}</Banner>}

      {/* Corrigo */}
      <Card>
        <CardHeader
          title="Corrigo Pro"
          subtitle="Scoped by Branch id + Customer id (CorrigoPro Direct API)."
          action={
            status == null ? (
              <Spinner />
            ) : status.corrigo ? (
              <Badge tone="green">Configured</Badge>
            ) : (
              <Badge tone="amber">Not configured</Badge>
            )
          }
        />
        <CardBody className="space-y-4">
          {status && !status.corrigo && (
            <Banner tone="warn">
              Corrigo is not configured. Set <code>CORRIGO_BASE_URL</code> and{' '}
              <code>CORRIGO_BEARER_TOKEN</code> to enable these exports.
            </Banner>
          )}
          <div className="grid gap-4 sm:grid-cols-2">
            <Field label="Branch id">
              <Input value={branchId} onChange={(e) => setBranchId(e.target.value)} placeholder="e.g. 12345" />
            </Field>
            <Field label="Customer id">
              <Input value={customerId} onChange={(e) => setCustomerId(e.target.value)} placeholder="e.g. 67890" />
            </Field>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!corrigoReady || busy !== null}
              onClick={() =>
                run('cp-loc', 'Corrigo locations', () =>
                  dataExportApi.corrigoLocations(branchId.trim(), customerId.trim()),
                )
              }
            >
              {busy === 'cp-loc' ? <Spinner /> : 'Export Locations (CSV)'}
            </Button>
            <Button
              variant="secondary"
              disabled={!corrigoReady || busy !== null}
              onClick={() =>
                run('cp-tra', 'Corrigo trades', () =>
                  dataExportApi.corrigoTrades(branchId.trim(), customerId.trim()),
                )
              }
            >
              {busy === 'cp-tra' ? <Spinner /> : 'Export Trades (CSV)'}
            </Button>
            <Button
              variant="secondary"
              disabled={!corrigoReady || busy !== null}
              onClick={() =>
                run('cp-pri', 'Corrigo priorities', () =>
                  dataExportApi.corrigoPriorities(branchId.trim(), customerId.trim()),
                )
              }
            >
              {busy === 'cp-pri' ? <Spinner /> : 'Export Priorities (CSV)'}
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Locations and Trades are paginated, then flattened (nested fields become{' '}
            <code>Parent_Child</code> columns) and cleaned. Trades (Corrigo "Specialties") carry their
            parent trade in <code>RelatedTo</code>. Priorities are tagged <code>Base</code> vs{' '}
            <code>Overridden</code>.
          </p>
        </CardBody>
      </Card>

      {/* ServiceChannel */}
      <Card>
        <CardHeader
          title="ServiceChannel"
          subtitle="Scoped by Subscriber id (v3 OData + workorders/priorities)."
          action={
            status == null ? (
              <Spinner />
            ) : status.servicechannel ? (
              <Badge tone="green">Configured</Badge>
            ) : (
              <Badge tone="amber">Not configured</Badge>
            )
          }
        />
        <CardBody className="space-y-4">
          {status && !status.servicechannel && (
            <Banner tone="warn">
              ServiceChannel is not configured. Set <code>SC_CLIENT_ID</code> and{' '}
              <code>SC_CLIENT_SECRET</code> (an app-level token is minted automatically).
            </Banner>
          )}
          <Field label="Subscriber id">
            <Input
              value={subscriberId}
              onChange={(e) => setSubscriberId(e.target.value)}
              placeholder="e.g. 5000123"
            />
          </Field>
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={!scReady || busy !== null}
              onClick={() =>
                run('sc-loc', 'ServiceChannel locations', () =>
                  dataExportApi.scLocations(subscriberId.trim()),
                )
              }
            >
              {busy === 'sc-loc' ? <Spinner /> : 'Export Locations (CSV)'}
            </Button>
            <Button
              variant="secondary"
              disabled={!scReady || busy !== null}
              onClick={() =>
                run('sc-tra', 'ServiceChannel trades', () => dataExportApi.scTrades(subscriberId.trim()))
              }
            >
              {busy === 'sc-tra' ? <Spinner /> : 'Export Trades (CSV)'}
            </Button>
            <Button
              variant="secondary"
              disabled={!scReady || busy !== null}
              onClick={() =>
                run('sc-pri', 'ServiceChannel priorities', () =>
                  dataExportApi.scPriorities(subscriberId.trim()),
                )
              }
            >
              {busy === 'sc-pri' ? <Spinner /> : 'Export Priorities (CSV)'}
            </Button>
          </div>
          <p className="text-xs text-slate-400">
            Locations and Trades share one OData paginator; Priorities is a single call.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
